import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { ScarcityIcon } from '#src/atoms/icons/ScarcityIcon/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineS, LabelL } from '#src/atoms/typography/index.tsx';
import { Row } from '#src/components/seo/ui.tsx';
import { format as formatSeason } from '#src/lib/seasons.ts';

import type { FullCardSupply_anyPlayerInterface } from './__generated__/index.graphql.ts';

const StyledHeadlineS = styled(HeadlineS)`
  width: 100%;
`;

const IconContainer = styled.div`
  width: 100%;
`;

const StyledRow = styled(Row)`
  & > *:not(:first-child) {
    text-align: right;
  }
`;

const SupplyRow = ({
  supply,
}: {
  supply: FullCardSupply_anyPlayerInterface['cardSupply'][number] | undefined;
}) => {
  if (!supply) return null;

  return (
    <StyledRow
      style={{
        gridTemplateColumns: `1fr repeat(4, var(--quadruple-unit))`,
      }}
    >
      <LabelL bold>{formatSeason(supply.season.startYear)}</LabelL>
      <LabelL>{supply.limited}</LabelL>
      <LabelL>{supply.rare}</LabelL>
      <LabelL>{supply.superRare}</LabelL>
      <LabelL>{supply.unique}</LabelL>
    </StyledRow>
  );
};

type Props = {
  anyPlayer: FullCardSupply_anyPlayerInterface;
};

export const FullCardSupply = ({ anyPlayer }: Props) => {
  const { cardSupply } = anyPlayer;

  if (cardSupply.length === 0) return null;

  const cardSupplyDesc = [...cardSupply].sort(
    (a, b) => b.season.startYear - a.season.startYear
  );

  return (
    <Vertical gap={1.5}>
      <StyledRow
        style={{
          gridTemplateColumns: `1fr repeat(4, var(--quadruple-unit))`,
          justifyItems: 'flex-end',
        }}
      >
        <StyledHeadlineS as="h2">
          <FormattedMessage defaultMessage="Cards supply" />
        </StyledHeadlineS>
        <IconContainer>
          <ScarcityIcon rarity={Rarity.limited} />
        </IconContainer>
        <IconContainer>
          <ScarcityIcon rarity={Rarity.rare} />
        </IconContainer>
        <IconContainer>
          <ScarcityIcon rarity={Rarity.super_rare} />
        </IconContainer>
        <IconContainer>
          <ScarcityIcon rarity={Rarity.unique} />
        </IconContainer>
      </StyledRow>
      {cardSupplyDesc.map(cs => (
        <SupplyRow key={cs.season.startYear.toString()} supply={cs} />
      ))}
    </Vertical>
  );
};

FullCardSupply.fragments = {
  anyPlayer: gql`
    fragment FullCardSupply_anyPlayerInterface on AnyPlayerInterface {
      slug
      cardSupply {
        limited
        rare
        superRare
        unique
        season {
          startYear
        }
      }
    }
  ` as TypedDocumentNode<FullCardSupply_anyPlayerInterface>,
};
